import { useState } from 'react';
import { Helmet } from 'react-helmet-async';

import { PATH_MISSION_QUOTATION_PRINTER } from '@e-socna/printer-contract';

import { useMissionAccess } from '../../components/Access/hooks/useMissionAccess.hook';
import {
  PATH_MISSION_DEFAULT,
  PATH_MISSION_QUOTATION_DEFAULT,
  PATH_MISSION_QUOTATION_EDITOR,
  PATH_MISSION_QUOTATION_GENERATION,
  PATH_MISSION_QUOTATION_INFO,
  PATH_MISSION_QUOTATION_INFO_EDIT,
  PATH_MISSION_QUOTATION_INFO_EDIT_STATE,
  PATH_MISSION_QUOTATION_PREVIEW,
  PATH_MISSION_QUOTATION_SHARE,
  PATH_MISSION_QUOTATION_SUMMARY,
  PATH_MISSION_QUOTATION_VERSIONS,
} from '../../app/constants/paths.constant';
import { Breadcrumbs } from '../../components/Breadcrumbs';
import { DocumentEditor } from '../../components/DocumentEditor';
import { Loading } from '../../components/Loading';
import { FormVariant } from '../../forms';
import { BackupMode } from '../../components/DocumentEditor/enums/backup-mode.enum';
import { useBuildPaths } from '../../hooks/useBuildPaths.hook';
import { useMissionReadyContext } from '../../contexts/MissionContext/hooks/useMissionReadyContext.hook';
import { useQuotationReadyContext } from '../../contexts/QuotationContext/hooks/useQuotationReadyContext.hook';
import { useMissionBreadcrumbPaths } from '../../hooks/useMissionBreadcrumbPaths.hook';
import { useMissionSpaceWording } from '../../hooks/useMissionSpaceWording.hook';
import { buildDocumentTitle } from '../../utils/document-title.util';
import { PageTitle } from '../../components/PageTitle';

import { useGetters } from './hooks/useGetters.hook';
import { MissionQuotationInfoTabPanel } from './Info';
import { MissionQuotationComplianceDialog } from './MissionQuotationComplianceDialog';
import { useSaveHandler } from './hooks/useSaveHandler.hook';
import { useSavePreviewHandler } from './hooks/useSavePreviewHandler.hook';
import { usePrintHandler } from './hooks/usePrintHandler.hook';
import { useSavePrintSettingsHandler } from './hooks/useSavePrintSettingsHandler.hook';

interface Props {
  readonly isInPrintMode: boolean;
}

const NO_TEMPLATE_ID = 'no-template';

export const MissionQuotationReady = ({ isInPrintMode }: Readonly<Props>) => {
  const { folderId, missionId, mission } = useMissionReadyContext();
  const access = useMissionAccess(missionId, mission);
  const missionWording = useMissionSpaceWording();
  const missionBreadcrumbPaths = useMissionBreadcrumbPaths();
  const { missionQuotationId, missionQuotation, missionQuotationInfo } =
    useQuotationReadyContext();

  const [isLoading, setIsLoading] = useState(false);

  const { paths: missionPaths } = useBuildPaths(
    {
      default: PATH_MISSION_DEFAULT,
      quotation: PATH_MISSION_QUOTATION_DEFAULT,
    },
    { folderId, missionId },
  );

  const { paths: editorModePaths } = useBuildPaths(
    {
      Info: PATH_MISSION_QUOTATION_INFO,
      InfoEdit: PATH_MISSION_QUOTATION_INFO_EDIT,
      InfoEditState: PATH_MISSION_QUOTATION_INFO_EDIT_STATE,
      Editor: PATH_MISSION_QUOTATION_EDITOR,
      Printer: PATH_MISSION_QUOTATION_PRINTER,
      Summary: PATH_MISSION_QUOTATION_SUMMARY,
      Preview: PATH_MISSION_QUOTATION_PREVIEW,
      Generation: PATH_MISSION_QUOTATION_GENERATION,
      Versions: PATH_MISSION_QUOTATION_VERSIONS,
      Share: PATH_MISSION_QUOTATION_SHARE,
    },
    { folderId, missionId },
  );

  const { onSave } = useSaveHandler({
    currentInfo: missionQuotationInfo,
    editorModePaths,
    missionQuotationId,
    setIsLoading,
  });

  const { onSavePreview } = useSavePreviewHandler({
    missionQuotationId,
    setIsLoading,
  });

  const { onPrint } = usePrintHandler({
    missionQuotationId,
    snackbarActionTarget: missionPaths.quotation,
  });

  const { onSavePrintSettings } = useSavePrintSettingsHandler({
    missionQuotationId,
  });

  const getters = useGetters();

  const quotationWithItsInfo = {
    ...missionQuotation,
    info: missionQuotationInfo,
  };

  return (
    <>
      {/* eslint-disable-next-line @typescript-eslint/no-unnecessary-type-conversion */}
      {!!isLoading && <Loading showBackdrop={false} variant="linear" />}
      {!isInPrintMode && <MissionQuotationComplianceDialog />}
      {!isInPrintMode && (
        <>
          <Helmet>
            <title>
              {buildDocumentTitle(
                `Fiche Devis ${missionWording.ofName}`,
                missionQuotation.code,
              )}
            </title>
          </Helmet>
          <Breadcrumbs
            paths={[
              ...missionBreadcrumbPaths,
              {
                to: missionPaths.quotation,
                label: 'devis',
                key: 'quotation',
              },
            ]}
          />
          <PageTitle
            code={missionQuotation.code}
            title={missionQuotation.title ?? missionQuotation.code}
          />
        </>
      )}
      <DocumentEditor
        componentSettings={{
          InfoTabPanel: MissionQuotationInfoTabPanel,
          welcomeMessage:
            'Votre devis est vide pour le moment. Commencez le travail !',
        }}
        formSettings={{
          formVariant: FormVariant.Modification,
          initialEditable: quotationWithItsInfo,
          isLoading,
        }}
        getters={getters}
        handlers={{
          onSavePreview,
          onSave,
          onPrint,
          onSavePrintSettings,
        }}
        reinitKey={`${missionQuotationId}-${missionQuotation.templateId ?? NO_TEMPLATE_ID}`}
        resolversSettings={{
          allowedReturnTypeIds: undefined,
          enableDirectGeneration: true,
          mustBeResolvableOnBack: false,
        }}
        routerSettings={{
          editorModePaths,
          gates: {
            infoEdit: access.updateQuotation,
            infoEditState: access.changeQuotationState,
            contentEdit: access.updateQuotationDocument,
          },
          isInPrintMode,
        }}
        saveSettings={{
          backupMode: BackupMode.Auto,
        }}
      />
    </>
  );
};
